import { disconnectDynamicPagePopupAction } from '@wix/bi-logger-wix-code-data/v2'
import { disconnectDynamicPagePanel as disconnectPanelBiData } from '@wix/wix-data-client-common/dist/esm/bi/panelDataForBi'
import openErrorPanel from '@wix/wix-data-client-common/dist/esm/panels/error-panel/openErrorPanel'
import { getAssetUrlInWorker } from '@wix/wix-data-client-common/dist/esm/panels/getAssetUrl'
import { getBiDefaults } from './getBiDefaults'
import { isDPCollectionDeleted } from './isDPCollectionDeleted'
import {
  resolveHasDpVariants,
  type AppApi,
  type EditorOriginToBI,
} from '@wix/wix-data-client-common'
import type {
  EditorSDK,
  FedopsLogger,
  I18n,
  Logger,
} from '@wix/wix-data-client-common-standalone'
import type { CollectionsApi } from '@wix/wix-code-collections-api'
import type { PageRef } from '@wix/editor-platform-sdk-types'
import { openDpVariantsRemoveConnectionsWarning } from '../openDpVariantsRemoveConnectionsWarning'

type Dependencies = {
  editorSdkProxy: EditorSDK
  collectionsApi: CollectionsApi
  logger: Logger
  appApi: AppApi
  i18n: I18n
  fedopsLogger: FedopsLogger
  isCollectionDeleted: (collectionId: string) => Promise<boolean>
}

type EventPayload = {
  pageRef: PageRef
  origin: EditorOriginToBI
}

export const openDisconnectPagePanel = async (
  {
    editorSdkProxy,
    collectionsApi,
    logger,
    appApi,
    i18n,
    fedopsLogger,
    isCollectionDeleted,
  }: Dependencies,
  { pageRef, origin }: EventPayload,
) => {
  const t = i18n.t.bind(i18n)
  const routerRef = await editorSdkProxy.document.routers.getByPage({
    pageRef,
  })

  const biDefaults = await getBiDefaults({
    editorSdkProxy,
    collectionsApi,
    pageRef,
    routerRef,
    origin,
    panelName: disconnectPanelBiData.panelName,
  })
  const disconnectDynamicPage = async () => {
    fedopsLogger.interactionStarted('disconnectDynamicPage')
    logger.bi(
      disconnectDynamicPagePopupAction({
        ...biDefaults,
        category: 'continue',
      }),
    )

    await appApi.disconnectDynamicPage({ pageRef, routerRef })

    fedopsLogger.interactionEnded('disconnectDynamicPage')
  }

  if (
    await isDPCollectionDeleted({
      editorSdkProxy,
      routerRef,
      pageRef,
      isCollectionDeleted,
    })
  ) {
    await disconnectDynamicPage()
    return
  }
  await openErrorPanel({
    editorSdkProxy,
    logger,
    helpId: 'e1add2cd-b7d4-45fa-b20f-57a3ad2c4609',
    headerText: t('Workspace_DYNAMIC_PAGES_WARNING_REMOVE_DATASET_TITLE'),
    topDescriptionText: t(
      'Workspace_Dynamic_Pages_Warning_Remove_Dataset_Paragraph1',
    ),
    bottomDescriptionText: undefined,
    mainActionText: t('Workspace_Dynamic_Pages_Warning_Remove_Dataset_Button2'),
    secondaryActionText: t(
      'Workspace_Dynamic_Pages_Warning_Remove_Dataset_Button1',
    ),
    illustration: await getAssetUrlInWorker(
      editorSdkProxy,
      'panel-icons/disconnect_dynamic_page.svg',
    ),
    onMainActionClick: async () => {
      const [routerByPage, hasDpVariants] = await Promise.all([
        editorSdkProxy.document.routers.getByPage({ pageRef }),
        resolveHasDpVariants({ pageRef, editorSdkProxy }),
      ])
      const isPageConnected = !!routerByPage
      if (hasDpVariants) {
        await openDpVariantsRemoveConnectionsWarning({
          logger,
          editorSdkProxy,
          i18n,
          collectionsApi,
          pageRef,
        })
        return
      }

      if (!isPageConnected) {
        return
      }

      await disconnectDynamicPage()
    },
    onSecondaryActionClick: () => {
      logger.bi(
        disconnectDynamicPagePopupAction({
          ...biDefaults,
          category: 'cancel',
        }),
      )
    },
    onCloseClick: () => {
      logger.bi(
        disconnectDynamicPagePopupAction({
          ...biDefaults,
          category: 'close',
        }),
      )
    },
  })
}
